/**
 * Runtime telemetry bootstrap for the merge-link SDK.
 *
 * Responsibilities:
 * - Keep the main bundle telemetry-free by exposing no-op capture helpers.
 * - Register a bridge on `window.mergeLinkTelemetry.enableTelemetry` that the
 *   external telemetry plugin can use to supply real Sentry/PostHog methods.
 * - Expose the current linking-flow step on that same bridge via `getContext`,
 *   so the plugin can stamp step identity onto every event it sends. The step
 *   state lives in the main bundle because that is where navigation happens;
 *   the plugin reads it rather than tracking it.
 * - Decide at runtime whether to inject the external telemetry plugin script,
 *   respecting customer kill switches (e.g. `window.MERGE_LINK_TELEMETRY === "off"`).
 * - Provide helper functions (`captureException`, `captureMessage`) for the rest
 *   of the SDK to call without knowing whether telemetry is active.
 *
 * How it works (high level):
 * 1) Host app calls the SDK initializer with `telemetry.enabled` (default true).
 * 2) The SDK forwards that choice automatically to the iframe (e.g., via URL
 *    query param like `?telemetry=off`); the host only sets `telemetry.enabled`.
 * 3) The iframe sets `window.MERGE_LINK_TELEMETRY` accordingly before calling
 *    `initializeTelemetry()`.
 * 4) `initializeTelemetry()` registers a bridge and, unless the kill switch is "off",
 *    injects the telemetry plugin script (external file; not part of this bundle).
 * 5) When the plugin is ready, it calls `window.mergeLinkTelemetry.enableTelemetry(...)`
 *    with real implementations; until then our `capture*` helpers are no-ops.
 */
import { getCdnOrigin } from "../utils/cdn";
import {
  LinkingFlowTelemetryContext,
  TelemetryBridge,
  TelemetryCaptureContext,
  TelemetryMethods,
} from "../telemetry/bridge";
import { getLinkingFlowContext } from "./stepTracking";

const noopTelemetry: TelemetryMethods = {
  captureException: () => undefined,
  captureMessage: () => undefined,
  capturePageview: () => undefined,
  setOrganization: () => undefined,
  setEndUser: () => undefined,
  resetIdentity: () => undefined,
};

// The plugin is injected asynchronously, so the first steps of the flow can run
// before PostHog exists. Those pageviews are the top of the funnel and dropping
// them would overstate completion, so hold them until the plugin arrives.
// Bounded: a flow that never loads the plugin must not grow this forever.
const MAX_BUFFERED_PAGEVIEWS = 25;
type BufferedPageview = {
  context: LinkingFlowTelemetryContext;
  properties?: Record<string, unknown>;
};
let bufferedPageviews: BufferedPageview[] = [];
let pendingOrganization: { id: string; name?: string } | undefined;
let pendingEndUserId: string | undefined;
// posthog.group() emits a $groupidentify every call, so sending it on each step
// would add a redundant event per transition.
let lastOrganizationId: string | undefined;
// identify() dedupes internally, but a repeat call still costs a request.
let lastEndUserId: string | undefined;

// The currently active telemetry implementations. Starts as no-ops and is
// replaced by the plugin at runtime via the bridge.
let activeTelemetry: TelemetryMethods = noopTelemetry;

/**
 * Bridge exposed to the external telemetry plugin. When the plugin loads, it
 * calls `enableTelemetry({ captureException, captureMessage })` to replace the
 * no-op implementations with real ones.
 */
const bridge: TelemetryBridge = {
  enableTelemetry: (methods: Partial<TelemetryMethods>) => {
    // Merge to allow the plugin to provide only the methods it supports.
    activeTelemetry = {
      ...activeTelemetry,
      ...methods,
    };

    // Identity first: a pageview replayed before it would be attributed to an
    // anonymous person and an ungrouped session.
    if (pendingEndUserId) {
      activeTelemetry.setEndUser(pendingEndUserId);
      pendingEndUserId = undefined;
    }

    if (pendingOrganization) {
      activeTelemetry.setOrganization(pendingOrganization.id, pendingOrganization.name);
      pendingOrganization = undefined;
    }

    const queued = bufferedPageviews;
    bufferedPageviews = [];
    queued.forEach(({ context, properties }) =>
      activeTelemetry.capturePageview(context, properties)
    );
  },
  getContext: () => getLinkingFlowContext(),
};

const ensureBridgeRegistered = () => {
  if (typeof window === "undefined") {
    return;
  }

  if (!window.mergeLinkTelemetry) {
    // Idempotent: only set the bridge once.
    window.mergeLinkTelemetry = bridge;
  }
};

/**
 * Determines which telemetry plugin URL to load. The plugin is served alongside the
 * bundle, so customers only control the boolean enable/disable flag. The CDN should
 * expose `telemetry-plugin.current.js` and overwrite it on release.
 */
const resolvePluginUrl = (): string | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }

  return `${getCdnOrigin()}/telemetry/telemetry-plugin.current.js`;
};

const shouldLoadPlugin = () => {
  if (typeof window === "undefined") {
    return false;
  }

  // Self-hosted builds (the Link image) never load it: the plugin reports to
  // Merge's Sentry and PostHog, and those builds don't ship it.
  if (process.env.REACT_APP_SELF_HOSTED === "true") {
    return false;
  }

  // Default ON. If a host wants telemetry off, it must set this to 'off'
  // BEFORE initializeTelemetry() runs (e.g., via iframe URL param parsing).
  if (window.MERGE_LINK_TELEMETRY === "off") {
    return false;
  }

  return true;
};

/**
 * Injects the telemetry plugin script exactly once. The plugin is responsible
 * for importing Sentry/PostHog and calling the bridge once it is ready.
 *
 * Notes:
 * - We tag the script with a data-attribute so re-entrant calls don't inject it twice.
 * - `crossOrigin="anonymous"` supports fetching across CDNs.
 * - Consider adding SRI in the HTML context if you want extra integrity checks.
 */
const injectTelemetryPlugin = () => {
  if (typeof document === "undefined") {
    return;
  }

  const pluginUrl = resolvePluginUrl();
  if (!pluginUrl) {
    return;
  }

  const existingScript = document.querySelector(
    `script[data-merge-telemetry-plugin="${pluginUrl}"]`
  );
  if (existingScript) {
    // Already injected; nothing to do.
    return;
  }

  const script = document.createElement("script");
  script.src = pluginUrl;
  script.async = true;
  script.crossOrigin = "anonymous";
  script.dataset.mergeTelemetryPlugin = pluginUrl;
  document.head.appendChild(script);
};

export const initializeTelemetry = () => {
  ensureBridgeRegistered();

  if (!shouldLoadPlugin()) {
    return;
  }

  injectTelemetryPlugin();
};

/**
 * SDK-facing helpers. These remain safe to call regardless of whether the
 * telemetry plugin loaded; by default they are no-ops.
 *
 * If the plugin loaded and called the bridge, these calls are forwarded to
 * the real implementations, otherwise they do nothing.
 */
export const captureException = (
  error: unknown,
  context?: TelemetryCaptureContext
) => {
  activeTelemetry.captureException(error, context);
};

export const captureMessage = (
  message: string,
  context?: TelemetryCaptureContext
) => {
  activeTelemetry.captureMessage(message, context);
};

/**
 * Emits one pageview for the step the user just landed on. Safe to call before
 * the plugin loads: the event is buffered and replayed once it does.
 */
export const capturePageview = (properties?: Record<string, unknown>) => {
  // Snapshot now. By the time a buffered pageview is sent the user has usually
  // moved on, and the event has to carry the step it was captured on.
  const context = { ...getLinkingFlowContext() };

  if (activeTelemetry === noopTelemetry) {
    if (bufferedPageviews.length < MAX_BUFFERED_PAGEVIEWS) {
      bufferedPageviews.push({ context, properties });
    }
    return;
  }

  activeTelemetry.capturePageview(context, properties);
};

/**
 * Attributes the flow to the Merge customer whose Link this is, so a funnel can
 * be filtered to one organization. Buffered like pageviews when the plugin has
 * not loaded yet.
 */
export const setTelemetryOrganization = (organizationId: string, organizationName?: string) => {
  if (organizationId === lastOrganizationId) {
    return;
  }
  lastOrganizationId = organizationId;

  if (activeTelemetry === noopTelemetry) {
    pendingOrganization = { id: organizationId, name: organizationName };
    return;
  }

  activeTelemetry.setOrganization(organizationId, organizationName);
};

/**
 * Identifies the end user this Link session belongs to. Takes the registered user
 * id only — see the bridge contract for why no origin id or email is sent.
 * Buffered like the organization when the plugin has not loaded yet.
 */
export const setTelemetryEndUser = (endUserId: string) => {
  if (endUserId === lastEndUserId) {
    return;
  }
  lastEndUserId = endUserId;

  if (activeTelemetry === noopTelemetry) {
    pendingEndUserId = endUserId;
    return;
  }

  activeTelemetry.setEndUser(endUserId);
};

/**
 * Whether telemetry is switched on for this session.
 *
 * Gates the fetch of end-user identity, so those identifiers are never requested
 * for a browser that has telemetry off. Deliberately reads the kill switch rather
 * than waiting for the plugin to hand over: the plugin can fail to load for
 * reasons that have nothing to do with consent (ad blocker, network policy), and
 * identity should still be ready if it recovers.
 */
export const isTelemetryEnabled = (): boolean => shouldLoadPlugin();

/**
 * Forgets the end user and customer identified so far.
 *
 * A single iframe can host more than one Link session — a new link token can be
 * pushed to it, and may belong to a different end user at a different customer.
 * Without this, the second session inherits the first one's person and group.
 *
 * Clears the pending buffers too: identity captured for the previous token must
 * not be replayed once the plugin arrives.
 */
export const resetTelemetryIdentity = () => {
  lastEndUserId = undefined;
  lastOrganizationId = undefined;
  pendingEndUserId = undefined;
  pendingOrganization = undefined;

  if (activeTelemetry === noopTelemetry) {
    return;
  }

  activeTelemetry.resetIdentity();
};

export const resetTelemetry = () => {
  // Useful for tests or teardown; restores the no-op implementations.
  activeTelemetry = noopTelemetry;
  bufferedPageviews = [];
  pendingOrganization = undefined;
  lastOrganizationId = undefined;
  pendingEndUserId = undefined;
  lastEndUserId = undefined;
};
